import { Alert, Anchor, Button, Center, Loader, Select, Stack, Text, TextInput } from '@mantine/core';
import { useMedplumContext } from '@medplum/react-hooks';
import { useCallback, useEffect, useRef, useState, type ComponentProps } from 'react';
import { Link, Navigate, useParams, useSearchParams } from 'react-router-dom';
import {
  chimeGuestLeave,
  releaseFailedGuestStart,
  chimeGuestStatus,
  chimeGuestWait,
  findAppointmentForLinkCode,
  guestJoinKeyStorageKey,
  guestJoinRoute,
  isTerminalGuestJoinError,
  releaseChimeGuestSessionOnUnload,
  signedInJoinTarget,
  type ChimeGuestWaitResult,
  type GuestJoinRoute,
} from '@/lib/telehealth/chime/chimeGuestService';
import { useChimeMeetingSession } from '@/lib/telehealth/chime/useChimeMeetingSession';
import { microphoneJoinState } from '@/lib/telehealth/chime/chimeDevices';
import { droppedCallNotice, REMOVED_FROM_SESSION_TEXT } from '@/lib/telehealth/chime/chimeMeetingStatus';
import { localizeWaitingRoomOpensMessage } from '@/lib/telehealth/joinWindow';
import { useDevicePreview } from '@/lib/telehealth/chime/useDevicePreview';
import { ChimeJoinPreview } from '@/pages/session/components/ChimeJoinPreview';
import { ChimeMediaStage } from '@/pages/session/components/ChimeMediaStage';
import { CallEndedScreen } from '@/pages/session/components/CallEndedScreen';
import { RECORDING_NOTICE_MID_CALL_LABEL, RecordingNoticeModal } from '@/pages/session/components/RecordingNoticeModal';
import { ClientLocationConfirmationModal } from '@/pages/client-portal/components/ClientLocationConfirmationModal';
import {
  isLocationInputError,
  LOCATION_INPUT_ERRORS,
  type LocationConfirmationInput,
} from '@unify/fhir-model/resources/client-location-confirmation';
import { ProviderAbsentNotice, ProviderLateNotice } from '@/pages/session/components/ProviderAbsentNotice';
import { chimeAttendeeDisplayName } from '@/lib/telehealth/chime/chimeParticipantVisuals';
import { midCallRecordingNoticeOpen } from '@/lib/telehealth/chime/chimeRecording';
import { useChimeRecordingShown } from '@/lib/telehealth/chime/useChimeRecordingShown';
import { config } from '@/config';
import {
  useIntakeClientAuth,
  useSeparateIntakeClient,
  type ClientCredentials,
} from './intake/useEnsureIntakeClientCredentials';

const WAIT_POLL_MS = 3_000;
/** In-call poll: provider presence, and whether this guest is still in the call. Recording comes from the roster. */
const STATUS_POLL_MS = 5_000;
/**
 * Guest join signs in as its own ClientApplication, never the SPA client (P7-A design Decision 38): the SPA client
 * has no membership, and this one's only grant is executing `chime-guest-join`.
 */
const GUEST_CLIENT_CREDENTIALS: ClientCredentials = {
  clientId: config.medplum.guestClientId,
  clientSecret: config.medplum.guestClientSecret,
  notConfigured: 'Guest join is not configured. Set VITE_MEDPLUM_GUEST_CLIENT_ID and VITE_MEDPLUM_GUEST_CLIENT_SECRET.',
};

/** The bot's location refusals, worded for the person filling in the form. */
function locationRefusalText(message: string): string {
  if (message.includes(LOCATION_INPUT_ERRORS.tooLong)) return 'One of the address fields is too long. Please shorten it.';
  if (message.includes(LOCATION_INPUT_ERRORS.state)) return 'Enter the 2-letter state code, for example CO.';
  if (message.includes(LOCATION_INPUT_ERRORS.attestation)) return 'Please confirm that you are at this address.';
  return 'Please enter your street, city, state and ZIP code.';
}

/**
 * Server-issued join key. Survives a refresh so a reload keeps this browser's waiting-room
 * entry; it is a secret, so it never goes in the URL or a log. Dropped when the guest leaves or
 * is removed: coming back is a new waiting-room entry.
 */
function storedJoinKey(token: string, p?: string): string | undefined {
  return sessionStorage.getItem(guestJoinKeyStorageKey(token, p)) ?? undefined;
}

function forgetJoinKey(token: string, p?: string): void {
  sessionStorage.removeItem(guestJoinKeyStorageKey(token, p));
}

/**
 * Unauthenticated `/join/:code[?p=<personal code>]`. The codes are bearer credentials, so they
 * stay in the URL and out of anything we log; entry is still gated on the provider admitting
 * this person.
 */
export function GuestJoinPage(): JSX.Element {
  const { code: token } = useParams<{ code: string }>();
  const [searchParams] = useSearchParams();
  const p = searchParams.get('p')?.trim() || undefined;
  const { medplum: appMedplum, profile, loading } = useMedplumContext();
  // Decided once Medplum has restored any stored login, then kept: the auth flow below (and
  // later token refreshes) briefly report loading again, which must not unmount the call.
  const routeRef = useRef<GuestJoinRoute>('loading');
  if (routeRef.current === 'loading') {
    routeRef.current = guestJoinRoute(profile, {
      // A dead stored login leaves `appMedplum.isLoading()` stuck true forever: Medplum's
      // refreshProfile() leaves its internal promise pending on failure. Only let it extend
      // loading while a login is actually still there to wait on, or a guest returning to
      // their link long after a stale app login was stored would spin forever.
      loading: (loading || appMedplum.isLoading()) && Boolean(appMedplum.getActiveLogin()),
      storedLogin: Boolean(appMedplum.getActiveLogin()),
    });
  }
  const route = routeRef.current;
  // A signed-in Patient or Practitioner: look the code up under their own identity first. Their
  // policy returns only appointments they may read, so anonymous callers never learn an id.
  const [signedInTarget, setSignedInTarget] = useState<ReturnType<typeof signedInJoinTarget>>();
  useEffect(() => {
    if (route !== 'signed-in' || !token || !profile) return undefined;
    let cancelled = false;
    findAppointmentForLinkCode(appMedplum, token)
      .catch(() => undefined)
      .then((appt) => !cancelled && setSignedInTarget(signedInJoinTarget(profile, appt)))
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [appMedplum, profile, route, token]);
  const separateClient = route === 'separate-client' || signedInTarget?.kind === 'guest';
  // Only an anonymous app-wide session may be signed in as the guest client; a signed-in
  // user's own session cannot run the guest bot, so never swap it out from under them.
  const appAuth = useIntakeClientAuth({ enabled: route === 'session', credentials: GUEST_CLIENT_CREDENTIALS });
  const separate = useSeparateIntakeClient(separateClient, GUEST_CLIENT_CREDENTIALS);
  const medplum = separateClient ? separate.client : route === 'session' ? appMedplum : undefined;
  const ready = separateClient ? Boolean(separate.client) : route === 'session' && appAuth.ready;
  const authError = separateClient ? separate.authError : appAuth.authError;
  const session = useChimeMeetingSession();

  const [phase, setPhase] = useState<
    | 'name'
    | 'preview'
    /** The bot needs the joiner's session location before it queues them. */
    | 'location'
    | 'waiting'
    | 'recording-notice'
    | 'connecting'
    | 'in-call'
    | 'left'
    | 'ended'
    /** A refusal polling cannot fix (expired/invalid link, finished session). */
    | 'closed'
  >('name');
  // No camera or microphone is touched on the Navigate, staff, invalid-link or name screens.
  const preview = useDevicePreview({ enabled: (route === 'session' || separateClient) && phase !== 'name' });
  /** Set once the guest accepts the recording notice; the bot withholds credentials until then. */
  const acknowledgedRef = useRef(false);
  /** The same, for rendering: an accepted notice is not shown again mid-call in this page session. */
  const [acknowledged, setAcknowledged] = useState(false);
  /**
   * The location a personal-link participant attested. Sent with every `wait` while they wait (the bot ignores it
   * once their row exists), so a row that expired or was removed is queued again with it rather than asking again.
   */
  const locationRef = useRef<LocationConfirmationInput | undefined>(undefined);
  const [locationError, setLocationError] = useState<string | null>(null);
  /** The form stays open (keeping what was typed) while the bot checks the location with `wait`. */
  const [savingLocation, setSavingLocation] = useState(false);
  const savingLocationRef = useRef(false);
  const [displayName, setDisplayName] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [earlyJoin, setEarlyJoin] = useState(false);
  const [providerLate, setProviderLate] = useState(false);
  // Recording comes from Chime's roster (Decision 39); the wait response's `recordingActive` covers the connect.
  const { recording, expect: expectRecording } = useChimeRecordingShown(session.recorderPresent);
  const [providerPresent, setProviderPresent] = useState(true);
  /** The call dropped: the device check offers Join again. */
  const [dropped, setDropped] = useState(false);
  /** The provider removed this guest from the call: the left screen says so. */
  const [removed, setRemoved] = useState(false);
  /** The phase as of the last render, or as just set by rejoin/a drop: a late poll answer checks it. */
  const phaseRef = useRef(phase);
  phaseRef.current = phase;
  /** Set by Leave: a poll already in flight must not act on its response. */
  const leftRef = useRef(false);

  const startedRef = useRef(false);
  /** One wait/connect at a time: a second admitted `wait` would re-mint and kick this attendee. */
  const pollInFlightRef = useRef(false);
  const sessionRef = useRef(session);
  sessionRef.current = session;
  const startRef = useRef(session.start);
  startRef.current = session.start;
  const stopPreviewRef = useRef(preview.stopPreview);
  stopPreviewRef.current = preview.stopPreview;
  const restartPreviewRef = useRef(preview.restartPreview);
  restartPreviewRef.current = preview.restartPreview;
  const audioDeviceId = preview.audioDeviceId;
  const videoDeviceId = preview.videoDeviceId;
  const startMuted = !preview.micOn;
  const cameraOff = !preview.cameraOn;

  const connect = useCallback(
    async (result: ChimeGuestWaitResult) => {
      if (!result.meeting || !result.attendee) return;
      setPhase('connecting');
      expectRecording(result.recordingActive ? true : undefined);
      await stopPreviewRef.current();
      const joined = await startRef.current(result.meeting, result.attendee, {
        audioDeviceId,
        videoDeviceId,
        requireAudio: true,
        startMuted,
        cameraOff,
      });
      if (leftRef.current) {
        sessionRef.current.stop();
        return;
      }
      if (joined) {
        setDropped(false);
        setPhase('in-call');
        return;
      }
      // The row was deleted when the attendee was minted, so a retry re-queues the guest. Release the
      // minted attendee first (as ClientChimeSession does), then retry only on a click: polling
      // would re-mint every few seconds.
      if (token && medplum) {
        await releaseFailedGuestStart(medplum, { token, joinKey: result.joinKey, attendeeId: result.attendee.AttendeeId });
      }
      startedRef.current = false;
      setError('Could not connect to the session. Check your camera and microphone, then try again.');
      setPhase('preview');
      restartPreviewRef.current();
    },
    [audioDeviceId, cameraOff, expectRecording, startMuted, videoDeviceId, medplum, token],
  );

  const poll = useCallback(async () => {
    if (!token || !medplum || pollInFlightRef.current) return;
    pollInFlightRef.current = true;
    try {
      const result = await chimeGuestWait(medplum, {
        token,
        joinKey: storedJoinKey(token, p),
        ...(p ? { p } : {}),
        displayName: displayName.trim() || undefined,
        ...(acknowledgedRef.current ? { recordingAcknowledged: true } : {}),
        ...(locationRef.current ? { location: locationRef.current } : {}),
      });
      if (leftRef.current) {
        // Leave fired while this call (often the post-acknowledgement mint) was still in
        // flight, so it could not know the attendee being minted here: clean it up now
        // rather than stranding it in the meeting.
        if (result.attendee?.AttendeeId && result.joinKey) {
          chimeGuestLeave(medplum, {
            token,
            joinKey: result.joinKey,
            attendeeId: result.attendee.AttendeeId,
          }).catch(() => undefined);
        }
        return;
      }
      if (result.joinKey) sessionStorage.setItem(guestJoinKeyStorageKey(token, p), result.joinKey);
      if (result.state === 'ended') {
        forgetJoinKey(token, p);
        await stopPreviewRef.current();
        setPhase('ended');
      } else if (result.state === 'needs-location') {
        setPhase('location');
      } else if (result.state === 'admitted' && result.needsAcknowledgement) {
        // A poll sent before the guest accepted must not raise the notice again; the next poll carries the acceptance.
        if (!acknowledgedRef.current) setPhase('recording-notice');
      } else if (result.state === 'admitted' && result.meeting && result.attendee && !startedRef.current) {
        startedRef.current = true;
        await connect(result);
      } else if (result.state === 'waiting') {
        // A refusal shown earlier (e.g. already waiting in another window) no longer applies.
        setError(null);
        setEarlyJoin(Boolean(result.earlyJoin));
        setProviderLate(Boolean(result.providerLate));
        setPhase('waiting');
      }
    } catch (e) {
      if (leftRef.current) return;
      const message = e instanceof Error ? localizeWaitingRoomOpensMessage(e.message) : 'Could not join this session';
      // The bot refused the attested location: the form (still open) shows why, and it is not re-sent.
      if (locationRef.current && isLocationInputError(message)) {
        locationRef.current = undefined;
        setLocationError(locationRefusalText(message));
        setPhase('location');
        return;
      }
      // Any other failure while the form is submitting (e.g. the waiting room is full) is shown there with the bot's
      // own text; the location is kept for the retry.
      if (savingLocationRef.current && !isTerminalGuestJoinError(message)) {
        setLocationError(message);
        return;
      }
      setError(message);
      if (isTerminalGuestJoinError(message)) {
        forgetJoinKey(token, p);
        await stopPreviewRef.current();
        setPhase('closed');
      }
    } finally {
      pollInFlightRef.current = false;
    }
  }, [connect, displayName, medplum, p, token]);

  /** Sends `wait` with the location; on success `poll` moves on to waiting, on a refusal the form shows why. */
  const confirmLocation = useCallback(
    async (values: Parameters<ComponentProps<typeof ClientLocationConfirmationModal>['onConfirmed']>[0]) => {
      locationRef.current = values;
      setLocationError(null);
      setSavingLocation(true);
      savingLocationRef.current = true;
      try {
        await poll();
      } finally {
        savingLocationRef.current = false;
        setSavingLocation(false);
      }
    },
    [poll],
  );

  const acknowledgeRecording = useCallback(async () => {
    acknowledgedRef.current = true;
    setAcknowledged(true);
    setPhase('waiting');
    await poll();
  }, [poll]);

  useEffect(() => {
    // Also polls during the recording notice: admission state must not go stale while the guest
    // is deciding whether to accept being recorded.
    if (phase !== 'waiting' && phase !== 'recording-notice') return;
    void poll();
    const id = setInterval(() => void poll(), WAIT_POLL_MS);
    // Background tabs throttle timers; catch up the moment the guest looks again.
    const onVisible = (): void => {
      if (document.visibilityState === 'visible') void poll();
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      clearInterval(id);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [phase, poll]);

  useEffect(() => {
    const joinKey = token ? storedJoinKey(token, p) : undefined;
    if (phase !== 'in-call' || session.endedByHost || !token || !joinKey || !medplum) return undefined;
    // Assigned below, after checkStatus is defined; checkStatus clears it on a terminal error.
    // eslint-disable-next-line prefer-const
    let id: ReturnType<typeof setInterval>;
    // A poll still in flight when the call drops (or the guest leaves) must not override that screen or the join key.
    let cancelled = false;
    const checkStatus = (): void => {
      chimeGuestStatus(medplum, { token, joinKey })
        .then((status) => {
          if (cancelled || leftRef.current || phaseRef.current !== 'in-call') return;
          if (status.state === 'unknown' || status.state === 'ended') {
            // Removed by the provider, or the session ended: the attendee is already gone.
            sessionRef.current.stop();
            forgetJoinKey(token, p);
            startedRef.current = false;
            setPhase(status.state === 'ended' ? 'ended' : 'left');
            return;
          }
          setProviderPresent(status.providerPresent !== false);
        })
        .catch((e: unknown) => {
          // An expired link or finished session will not recover; keep the call, stop asking.
          if (e instanceof Error && isTerminalGuestJoinError(e.message)) clearInterval(id);
        });
    };
    // Connecting can take longer than STATUS_POLL_MS: check right away so a recording that
    // started while connecting is caught before the first scheduled tick.
    checkStatus();
    id = setInterval(checkStatus, STATUS_POLL_MS);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, [medplum, p, phase, session.endedByHost, token]);

  const handleJoin = useCallback(async () => {
    setBusy(true);
    setError(null);
    try {
      await poll();
    } finally {
      setBusy(false);
    }
  }, [poll]);

  // See ClientChimeSession: a closed tab or in-app navigation away must release both the
  // waiting claim and the attendee.
  const releaseRef = useRef<() => void>(() => undefined);
  useEffect(() => {
    if (
      !token ||
      !medplum ||
      (phase !== 'waiting' && phase !== 'recording-notice' && phase !== 'connecting' && phase !== 'in-call')
    ) {
      releaseRef.current = () => undefined;
      return undefined;
    }
    const onHide = (): void => {
      releaseChimeGuestSessionOnUnload(medplum, {
        token,
        joinKey: storedJoinKey(token, p),
        ...(session.attendeeId ? { attendeeId: session.attendeeId } : {}),
      });
    };
    releaseRef.current = onHide;
    window.addEventListener('pagehide', onHide);
    return () => window.removeEventListener('pagehide', onHide);
  }, [medplum, p, phase, session.attendeeId, token]);
  useEffect(() => () => releaseRef.current(), []);

  /** Explicit Leave or declining the recording notice: gives up the spot for good. */
  const handleLeave = useCallback(async () => {
    leftRef.current = true;
    const joinKey = token ? storedJoinKey(token, p) : undefined;
    if (token && joinKey && medplum) {
      try {
        await chimeGuestLeave(medplum, {
          token,
          joinKey,
          ...(session.attendeeId ? { attendeeId: session.attendeeId } : {}),
        });
      } catch {
        /* already gone */
      }
      forgetJoinKey(token, p);
    }
    releaseRef.current = () => undefined;
    session.stop();
    // Leaving from the waiting screen: the local camera/microphone preview is still running.
    await stopPreviewRef.current();
    startedRef.current = false;
    setPhase('left');
  }, [medplum, p, session, token]);

  /** Back to the device check; also after an end, since the provider may start the session again. */
  const rejoin = useCallback(() => {
    leftRef.current = false;
    acknowledgedRef.current = false;
    setAcknowledged(false);
    startedRef.current = false;
    setProviderPresent(true);
    sessionRef.current.stop();
    setError(null);
    setRemoved(false);
    // A new join starts over: the bot asks again only if this participant still has no confirmation.
    locationRef.current = undefined;
    setLocationError(null);
    phaseRef.current = 'preview';
    setPhase('preview');
    restartPreviewRef.current();
  }, []);

  // The connection dropped mid-call: back to the device check, as Rejoin does. The join key is kept, so the
  // same waiting-room entry is used again rather than a new one; the bot still queues an entry that was in the
  // call for admission again. The attendee is released (a non-final leave, which keeps the
  // entry) unless it is live on another device, where releasing it would drop that device. A guest the provider
  // removed sees the left screen instead, as the status poll showed it: the attendee is gone and the entry closed, so
  // the key is dropped and coming back is a new waiting-room entry.
  useEffect(() => {
    if (phase !== 'in-call' || session.status !== 'error') return;
    const notice = droppedCallNotice(session.errorCode, 'guest');
    if (notice.removed) {
      sessionRef.current.stop();
      if (token) forgetJoinKey(token, p);
      startedRef.current = false;
      setRemoved(true);
      phaseRef.current = 'left';
      setPhase('left');
      return;
    }
    if (!notice.elsewhere && token && medplum && session.attendeeId) {
      releaseChimeGuestSessionOnUnload(medplum, {
        token,
        joinKey: storedJoinKey(token, p),
        attendeeId: session.attendeeId,
      });
    }
    rejoin();
    setError(notice.message);
    setDropped(true);
  }, [medplum, p, phase, rejoin, session.attendeeId, session.errorCode, session.status, token]);

  // Never join with the SDK's silent microphone: wait for the prompt, and require a device.
  const microphone = microphoneJoinState(preview.access, preview.audioDeviceId);
  // Recording started after admission (e.g. the provider's Resume): the notice, client-side; Leave declines.
  const midCallNoticeOpen = midCallRecordingNoticeOpen({
    inCall: phase === 'in-call' && session.status === 'in-meeting',
    recording: session.recorderPresent,
    acknowledged,
  });

  if (!token) {
    return (
      <Center h="100dvh">
        <Text c="dimmed">This link is not valid.</Text>
      </Center>
    );
  }

  if (route === 'signed-in' && !signedInTarget) {
    return (
      <Center h="100dvh">
        <Loader size="lg" />
      </Center>
    );
  }

  // A signed-in client of this appointment joins through the portal under their own identity.
  if (signedInTarget?.kind === 'portal') {
    return <Navigate to={`/client-portal/session/${encodeURIComponent(signedInTarget.appointmentId)}/join`} replace />;
  }

  if (signedInTarget?.kind === 'staff') {
    return (
      <Center h="100dvh" p="md">
        <Stack align="center" gap="sm" maw={420}>
          <Text ta="center">
            This is a client or guest join link. You&apos;re signed in as staff, so join the
            session from the appointment instead.
          </Text>
          {signedInTarget.appointmentId ? (
            <Anchor
              component={Link}
              to={`/dashboard/appointments/${encodeURIComponent(signedInTarget.appointmentId)}`}
            >
              Open the appointment
            </Anchor>
          ) : null}
        </Stack>
      </Center>
    );
  }

  // Before left: once the host ends the call, a stale status answer must not relabel it.
  // `endedByHost` only counts in-call: it stays set until the next start, so after Rejoin the
  // device check must show.
  if (phase === 'ended' || (session.endedByHost && phase === 'in-call')) {
    return (
      <CallEndedScreen
        message="The provider ended this session. If they start it again, you can rejoin."
        doneLabel="Rejoin"
        onDone={rejoin}
      />
    );
  }

  if (phase === 'closed') {
    return (
      <Center h="100dvh" p="md">
        <Text ta="center" maw={420}>
          {error}
        </Text>
      </Center>
    );
  }

  if (phase === 'left') {
    return (
      <Center h="100dvh" p="md">
        <Stack align="center" gap="md" maw={420}>
          <Text ta="center">{removed ? REMOVED_FROM_SESSION_TEXT : 'You have left the call.'}</Text>
          <Button variant="light" onClick={rejoin}>
            Join the waiting room again
          </Button>
        </Stack>
      </Center>
    );
  }

  if (!ready) {
    return (
      <Center h="100dvh">
        <Stack align="center" gap="md">
          {authError ? <Text c="red" ta="center" maw={420}>{authError}</Text> : <Loader size="lg" />}
        </Stack>
      </Center>
    );
  }

  if (phase === 'in-call' || session.status === 'in-meeting') {
    return (
      <Stack h="100dvh" gap={0} style={{ overflow: 'hidden' }}>
        <RecordingNoticeModal
          opened={midCallNoticeOpen}
          acknowledgeLabel={RECORDING_NOTICE_MID_CALL_LABEL}
          onAcknowledge={() => {
            acknowledgedRef.current = true;
            setAcknowledged(true);
          }}
          onDecline={() => void handleLeave()}
        />
        {providerPresent ? null : <ProviderAbsentNotice />}
        {error || session.error ? (
          <Text c="red" px="md" pt="sm">
            {error ?? session.error}
          </Text>
        ) : null}
        <ChimeMediaStage
          tiles={session.tiles}
          bindTile={session.bindTile}
          bindAudio={session.bindAudio}
          muted={session.muted}
          cameraOn={session.cameraOn}
          onToggleMute={session.toggleMute}
          onToggleCamera={() => void session.toggleCamera()}
          onEnd={() => void handleLeave()}
          activeSpeakerId={session.activeSpeakerId}
          attendeeExternalUserIds={session.attendeeExternalUserIds}
          localDisplayName={displayName.trim() || 'Guest'}
          displayNameForExternalUserId={chimeAttendeeDisplayName}
          recording={recording}
          screenShare={{
            sharing: session.sharing,
            onStart: () => void session.startScreenShare(),
            onStop: session.stopScreenShare,
          }}
          localAttendeeId={session.attendeeId}
          endLabel="Leave call"
        />
      </Stack>
    );
  }

  return (
    <>
      <ClientLocationConfirmationModal
        opened={phase === 'location'}
        onClose={() => {
          if (savingLocation) return;
          // Cancelled: a location kept from a failed save must not be sent by the next Join without the form.
          locationRef.current = undefined;
          setLocationError(null);
          setPhase('preview');
        }}
        onConfirmed={confirmLocation}
        saving={savingLocation}
        saveError={locationError}
      />
      <RecordingNoticeModal
        opened={phase === 'recording-notice'}
        onAcknowledge={() => void acknowledgeRecording()}
        onDecline={() => void handleLeave()}
      />
      <Center h="100dvh" p="md" style={{ overflowY: 'auto' }}>
        <Stack align="center" gap="md" maw={600} w="100%">
          <Text size="lg" fw={500}>
            {phase === 'waiting'
              ? 'Waiting for the provider'
              : phase === 'connecting'
                ? 'Connecting…'
                : 'Join your session'}
          </Text>

          {error ? (
            <Alert color="red" w="100%">
              {error}
            </Alert>
          ) : null}

          {phase === 'name' ? (
            <>
              <Text size="sm" c="dimmed" ta="center">
                Enter your name so the provider knows who is waiting.
              </Text>
              <TextInput
                label="Your name"
                value={displayName}
                onChange={(e) => setDisplayName(e.currentTarget.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && displayName.trim()) setPhase('preview');
                }}
                maxLength={80}
                w="100%"
                data-autofocus
              />
              <Button size="lg" disabled={!displayName.trim()} onClick={() => setPhase('preview')}>
                Continue
              </Button>
            </>
          ) : (
            <>
              <ChimeJoinPreview preview={preview} />
              {preview.error ? <Text c="red" size="sm">{preview.error}</Text> : null}
              {preview.access === 'blocked' ? (
                <Button variant="light" onClick={preview.requestAccess}>
                  Allow camera &amp; microphone
                </Button>
              ) : null}
              <Select
                label="Microphone"
                data={preview.audioDevices.map((d) => ({ value: d.deviceId, label: d.label }))}
                value={preview.audioDeviceId}
                onChange={(v) => v && preview.setAudioDeviceId(v)}
                disabled={preview.audioDevices.length === 0}
                placeholder={preview.audioDevices.length === 0 ? 'No microphone detected' : undefined}
                w="100%"
              />
              <Select
                label="Camera"
                data={preview.videoDevices.map((d) => ({ value: d.deviceId, label: d.label }))}
                value={preview.videoDeviceId}
                onChange={(v) => v && preview.setVideoDeviceId(v)}
                disabled={preview.videoDevices.length === 0}
                placeholder={preview.videoDevices.length === 0 ? 'No camera detected' : undefined}
                w="100%"
              />
              {phase === 'connecting' ? (
                <Loader size="sm" />
              ) : phase === 'waiting' ? (
                <>
                  {earlyJoin ? (
                    <Alert color="yellow" w="100%">
                      Your session hasn&apos;t started yet. You can wait here — the provider will
                      admit you when they&apos;re ready.
                    </Alert>
                  ) : null}
                  {providerLate ? <ProviderLateNotice /> : null}
                  <Text size="sm" c="dimmed" ta="center">
                    You&apos;ll join automatically once the provider lets you in.
                  </Text>
                </>
              ) : (
                <>
                  {microphone.notice ? (
                    <Text c="red" size="sm" ta="center">
                      {microphone.notice}
                    </Text>
                  ) : null}
                  <Button
                    size="lg"
                    loading={busy}
                    disabled={microphone.disabled}
                    onClick={() => void handleJoin()}
                  >
                    {dropped ? 'Join again' : 'Join waiting room'}
                  </Button>
                </>
              )}
            </>
          )}
        </Stack>
      </Center>
    </>
  );
}
